import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { usePageMeta } from "../lib/i18n";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30;  // ~60s

export default function Merci() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null, error: null });
  const stopRef = useRef(false);

  usePageMeta({ titleKey: "meta.contact.title", descKey: "meta.contact.desc", path: "/merci" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", attempts: 0, data: null, error: "Pas d'identifiant de session." });
      return;
    }
    stopRef.current = false;
    const poll = async (attempt) => {
      if (stopRef.current) return;
      if (attempt >= MAX_ATTEMPTS) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
        if (r.ok) {
          const d = await r.json();
          if (d.payment_status === "paid") {
            setState({ status: "paid", attempts: attempt, data: d, error: null });
            return;
          }
          if (d.status === "expired") {
            setState({ status: "expired", attempts: attempt, data: d, error: null });
            return;
          }
        }
      } catch { /* keep polling */ }
      setState((s) => ({ ...s, attempts: attempt + 1 }));
      setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
    };
    poll(0);
    return () => { stopRef.current = true; };
  }, [sessionId]);

  return (
    <main data-testid="merci-page" className="bg-bone text-ink min-h-screen pt-32 pb-20">
      <section className="container-x max-w-2xl mx-auto text-center">
        {state.status === "polling" && (
          <FadeIn>
            <Loader2 size={36} className="mx-auto animate-spin text-stone mb-6" />
            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3">
              Vérification du paiement…
            </h1>
            <p className="text-ink/65">Merci de patienter quelques secondes.</p>
          </FadeIn>
        )}

        {state.status === "paid" && (
          <FadeIn>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-600 mb-6">
              <Check size={32} />
            </div>
            <div className="eyebrow mb-3" style={{ color: "#4A4E57" }}>Paiement confirmé</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mb-6" data-testid="merci-title">
              Merci pour votre <span className="italic text-stone">commande</span>.
            </h1>
            {state.data?.order && (
              <div className="bg-ink/5 border border-ink/10 rounded-md p-6 my-8 text-left max-w-md mx-auto">
                <div className="text-[10px] tracking-[0.22em] uppercase text-ink/55 mb-2">Récapitulatif</div>
                <div className="font-display text-2xl mb-1">{state.data.order.product_title}</div>
                <div className="text-sm text-ink/65 mb-3">{state.data.order.size_label}</div>
                <div className="font-display text-3xl">{state.data.order.total_eur?.toFixed(2)} €</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-ink/45 mt-2">
                  Commande nº {sessionId?.slice(-8).toUpperCase()}
                </div>
              </div>
            )}
            <p className="text-ink/75 leading-relaxed max-w-md mx-auto mb-8">
              Une confirmation détaillée vient d{"'"}être envoyée à votre adresse email.
              Votre tirage sera imprimé et expédié sous 2 à 5 jours ouvrés.
            </p>
            <Link
              to="/boutique"
              data-testid="merci-back"
              className="inline-flex items-center gap-2 bg-ink text-bone hover:bg-ink/90 px-6 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors"
            >
              Retour à la boutique <ArrowRight size={14} />
            </Link>
          </FadeIn>
        )}

        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <FadeIn>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/15 text-red-600 mb-6">
              <AlertCircle size={32} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-4">
              Paiement non confirmé
            </h1>
            <p className="text-ink/65 mb-8">
              {state.status === "expired"
                ? "La session de paiement a expiré."
                : state.status === "timeout"
                ? "Délai de vérification dépassé. Vérifiez votre email, la commande peut être valide."
                : state.error || "Une erreur est survenue."}
            </p>
            <Link to="/boutique" className="inline-flex items-center gap-2 border border-ink/20 hover:bg-ink hover:text-bone px-6 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors">
              Retour à la boutique <ArrowRight size={14} />
            </Link>
          </FadeIn>
        )}
      </section>
    </main>
  );
}
